import { Category, Survey } from '@models/Survey'
import { isSanteApp } from '@models/AppType'

export type QuestionnairePhase = 'q1' | 'q2' | 'done'

export type ResultVariant = 'intermediate' | 'final'

export const PREDICTION_MODEL_SCAN26 = 'SCAN26'
export const PREDICTION_MODEL_EPQC = 'EPQC'

export type CategorySurveyRef = {
  id: string
  key: string
  order: number
  /** Resolved display name (string) or legacy localized map `{ fr, en }`. */
  name?: string | Record<string, string>
  names?: Record<string, string>
  /** Active questions in this questionnaire (from API `question_count`). */
  questionCount?: number
  question_count?: number
}

export function categorySurveyQuestionCount(ref: CategorySurveyRef): number | undefined {
  const n = ref.questionCount ?? ref.question_count
  return n != null && n > 0 ? n : undefined
}

/** Resolve survey display name for the active UI locale. */
export function categorySurveyDisplayName(
  ref: CategorySurveyRef,
  locale = 'fr',
): string | undefined {
  const lang = (locale || 'fr').substring(0, 2)
  const fromNames = ref.names?.[lang] ?? ref.names?.fr ?? ref.names?.en
  if (typeof fromNames === 'string' && fromNames.trim()) {
    return fromNames
  }
  if (typeof ref.name === 'string' && ref.name.trim()) {
    return ref.name
  }
  if (ref.name && typeof ref.name === 'object') {
    const map = ref.name as Record<string, string>
    const picked = map[lang] ?? map.fr ?? map.en ?? Object.values(map).find((v) => typeof v === 'string')
    if (typeof picked === 'string' && picked.trim()) {
      return picked
    }
  }
  return undefined
}

/** Strip leading edition prefix from survey names ("EQUE26 - Socio" → "Socio"). */
export function stripEditionSurveyLabel(label: string, category?: Category | null): string {
  const raw = label.trim()
  if (!raw) {
    return raw
  }
  const prefixes = [
    category?.scopeCode,
    category?.scope_code,
    category?.key,
    category?.urlSlug,
    category?.url_slug,
  ].filter((p): p is string => typeof p === 'string' && p.trim().length > 0)

  let next = raw
  for (const prefix of prefixes) {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const re = new RegExp(`^${escaped}\\s*[-–—:]\\s*`, 'i')
    next = next.replace(re, '')
  }
  next = next.replace(/^[A-Z]{2,}\d{2}\s*[-–—:]\s*/i, '')
  return next.trim() || raw
}

export function getCategory(survey?: Survey | null): Category | null {
  return survey?.category ?? null
}

export function categoryPredictionModel(category?: Category | null): string | undefined {
  if (!category) {
    return undefined
  }
  return category.predictionModel ?? category.prediction_model
}

/** Category exposes several active questionnaires (SCAN26, EQUE26 A·B·C, …). */
export function categoryHasMultipleSurveys(category?: Category | null): boolean {
  if (!category) {
    return false
  }
  if (category.multiSurvey != null) {
    return category.multiSurvey
  }
  const surveys = category.activeSurveys ?? category.active_surveys
  return surveys != null && surveys.length > 1
}

export function categoryHasMultipleSurveysFromSurvey(survey?: Survey | null): boolean {
  return categoryHasMultipleSurveys(getCategory(survey))
}

export function categoryUsesDsmpPrediction(category?: Category | null): boolean {
  if (categoryPredictionModel(category) === PREDICTION_MODEL_SCAN26) {
    return true
  }
  // Local / legacy payloads before `prediction_model=SCAN26` is set in admin
  if (isSanteApp && category?.key === 'scan26') {
    return true
  }
  return false
}

/** EQUE26 vote models A/B/C as sequential questionnaires (socio → issues → unrestricted). */
export function categoryUsesProgressiveVoteModels(category?: Category | null): boolean {
  if (!categoryHasMultipleSurveys(category)) {
    return false
  }
  if (categoryPredictionModel(category) === PREDICTION_MODEL_EPQC) {
    return true
  }
  const scope = (category?.scopeCode ?? category?.scope_code ?? '').toUpperCase()
  if (scope === 'EQUE26') {
    return true
  }
  const slug = (category?.urlSlug ?? category?.url_slug ?? '').toLowerCase()
  const key = (category?.key ?? '').toLowerCase()
  return (
    slug === 'eque26' ||
    slug.includes('eque26') ||
    /quebec[-_]?2026/.test(slug) ||
    key === 'eque26' ||
    key === 'eque26-cat' ||
    key.includes('eque26') ||
    key.includes('quebec-2026')
  )
}

/** Probability + citizen forecasting — election editions only, not SCAN26/DSMP. */
export function categoryHasProbabilityForecasting(category?: Category | null): boolean {
  return !categoryUsesDsmpPrediction(category)
}

/** After acknowledge on a non-final stage, skip the electoral « prediction » confirmation step. */
export function categorySkipsPredictionStep(
  category?: Category | null,
  survey?: Survey | null,
): boolean {
  if (categoryUsesDsmpPrediction(category)) {
    return true
  }
  // Intermediate EQUE26 stages: score best available model then continue (like SCAN26 Q1).
  if (categoryUsesProgressiveVoteModels(category) && hasNextSurveyInCategory(survey)) {
    return true
  }
  return false
}

/** @deprecated Use categorySkipsPredictionStep */
export function categorySkipsElectoralTunnel(category?: Category | null): boolean {
  return categorySkipsPredictionStep(category)
}

export function sortedCategorySurveys(category?: Category | null): CategorySurveyRef[] {
  const surveys = category?.activeSurveys ?? category?.active_surveys
  if (!surveys?.length) {
    return []
  }
  return [...surveys].sort((a, b) => a.order - b.order || a.key.localeCompare(b.key))
}

export function getNextSurveyKey(currentSurvey?: Survey | null): string | null {
  if (!currentSurvey) {
    return null
  }
  const sorted = sortedCategorySurveys(getCategory(currentSurvey))
  if (!sorted.length) {
    return null
  }
  let idx = sorted.findIndex((s) => s.id === currentSurvey.id)
  if (idx < 0) {
    // Current survey missing from active_surveys (often deactivated stage-1 while
    // later stages stay active). Continue by order so Q2/Q3 remain reachable.
    const order = currentSurvey.order ?? 0
    const nextByOrder = sorted.find((s) => s.order > order)
    return nextByOrder?.key ?? null
  }
  if (idx >= sorted.length - 1) {
    return null
  }
  return sorted[idx + 1].key
}

export function hasNextSurveyInCategory(survey?: Survey | null): boolean {
  return getNextSurveyKey(survey) != null
}

export function isLastSurveyInCategory(survey?: Survey | null): boolean {
  return categoryHasMultipleSurveysFromSurvey(survey) && !hasNextSurveyInCategory(survey)
}

export function surveyIndexInCategory(survey?: Survey | null): number {
  if (!survey) {
    return -1
  }
  return sortedCategorySurveys(getCategory(survey)).findIndex((s) => s.id === survey.id)
}

export function questionnairePhaseForSurvey(survey?: Survey | null): QuestionnairePhase {
  if (!categoryHasMultipleSurveysFromSurvey(survey) || !survey) {
    return 'q1'
  }
  const idx = surveyIndexInCategory(survey)
  const count = sortedCategorySurveys(getCategory(survey)).length
  if (idx < 0) {
    return 'q1'
  }
  if (idx >= count - 1) {
    return 'done'
  }
  if (idx === 0) {
    return 'q1'
  }
  return 'q2'
}

export function resolveResultVariant(
  explicit: ResultVariant | undefined,
  survey?: Survey | null,
  _phase?: QuestionnairePhase,
): ResultVariant {
  if (explicit) {
    return explicit
  }
  if (survey && categoryHasMultipleSurveysFromSurvey(survey)) {
    return isLastSurveyInCategory(survey) ? 'final' : 'intermediate'
  }
  return 'final'
}

export function dsmpScoreFromPrediction(prediction: unknown): number | undefined {
  if (typeof prediction !== 'object' || prediction === null) {
    return undefined
  }
  const record = prediction as { dsmp_score?: number; dsmpScore?: number }
  const score = record.dsmpScore ?? record.dsmp_score
  return typeof score === 'number' ? score : undefined
}

export function isDsmpPrediction(
  prediction: unknown,
): prediction is { dsmp_score?: number; dsmpScore?: number; phase: QuestionnairePhase } {
  return dsmpScoreFromPrediction(prediction) != null
}
